import { ECONOMY } from '@puzzlemania/blockjam';
import type { EconomyService } from '@puzzlemania/economy';
import { REMOTE_CONFIG_KEYS, type RemoteConfigService } from '@puzzlemania/remote-config';

/**
 * Module 45 (WP-45.6) — a new player's opening balance.
 *
 * ## The bug
 *
 * BUG-005: every player started on 0 coins. Not a regression — there was no
 * starting-grant path anywhere in the codebase. `economy.store.ts` and
 * `wallet-save-module.ts` both default to `{ coins: 0 }`, and nothing ever
 * credited the difference. Since WP-43.10 gave the power-ups real prices
 * (50/150/300), a new player could see three things they could not afford and
 * no way to earn the first one except by playing levels.
 *
 * ## Why this is a shared helper and not two `credit()` calls
 *
 * There are two composition roots — the main site's and the CrazyGames embed's
 * — and they have almost nothing in common on purpose (`composeEmbedServices`
 * must never reach Supabase). Two copies of a grant rule is two places for the
 * amount, the source id and the "is this player new" test to drift apart, and
 * the failure is silent in both directions: a doubled grant looks like
 * generosity, a missing one looks like the bug being fixed here.
 *
 * ## What decides "new"
 *
 * The caller passes `hasExistingWallet` — on both roots that is
 * `walletSaveModule.load() !== null` after `SaveService.loadAll()` has run. It
 * is a good signal and not a guarantee: a data provider that answers `null` for
 * a failed read is indistinguishable, from here, from one answering `null`
 * because the player is genuinely new. That is why the server, not this
 * function, is what makes the grant once-ever on the main site — see
 * `supabase/migrations/0010_starting_grant.sql`, which raises `already_exists`
 * on a second grant regardless of what the client believed.
 *
 * The embed has no server, so there the client's judgement IS the guarantee.
 * That is acceptable for the same reason the embed has no server-validated
 * economy at all: its wallet lives in the CrazyGames Data module, and a player
 * who can forge a read failure there is already editing their own save.
 */

/**
 * The ledger entry a starting grant is booked under.
 *
 * `system_` because this is the platform seeding a wallet, not a game paying
 * out and not a shop selling anything — and, more concretely, because
 * `EconomyService.credit()` THROWS on an unregistered prefix
 * (`packages/economy/src/valid-prefixes.ts`). A bare `'starting_grant'`, which
 * is what this WP was planned with, is not a valid id and would have turned the
 * first boot of a new install into an exception.
 */
export const STARTING_GRANT_SOURCE_ID = 'system_starting_grant';

/**
 * Used when remote config has no usable value.
 *
 * Not merely a defensive default: the server allowlist pins the grant to this
 * exact literal, so a fallback that differed from the shipped config would be
 * a value the server refuses. Keep this, `DEV_RC_DEFAULTS`, the Mock provider's
 * `DEV_DEFAULTS`, `portal-config.json` and the newest economy migration all
 * equal — the tests beside this file check every one of those pairings.
 *
 * Module 50 (WP-50.25) — 100 → 10, with the reward curve. 100 was a tenth of
 * one level's payout; against a 5–40 coin level it is two to three levels of
 * income, which is the point: a new player starts with something rather than
 * with a fifth of a power-up they cannot buy for another twenty levels.
 *
 * Module 97 — 10 → 100 with the ×10 coin scale (same ratio to a level).
 * Module 129 — read from the Blockway price registry.
 */
export const STARTING_COINS_FALLBACK: number = ECONOMY.rewards.startingCoins;

export interface StartingGrantOptions {
  economy: EconomyService;
  remoteConfig: RemoteConfigService;
  /** `walletSaveModule.load() !== null` — i.e. this uid has been seen before. */
  hasExistingWallet: boolean;
}

/**
 * Reads the configured amount, rejecting anything that is not a positive whole
 * number.
 *
 * Remote config is typed `unknown` at the provider boundary and reaches here
 * through `getNumber`, which does not validate — a row hand-edited to `"100"`,
 * `0`, `-50` or `null` in the admin dashboard would otherwise become a credit
 * of `NaN`, a pointless zero-coin transaction, or a *negative* credit that the
 * client applies optimistically and the server then rejects, leaving the two
 * disagreeing. Falling back to the shipped literal keeps every one of those
 * cases at the one amount the allowlist accepts.
 */
function configuredStartingCoins(remoteConfig: RemoteConfigService): number {
  const raw = remoteConfig.getNumber(REMOTE_CONFIG_KEYS.STARTING_COINS, STARTING_COINS_FALLBACK);
  const usable = typeof raw === 'number' && Number.isInteger(raw) && raw > 0;
  return usable ? raw : STARTING_COINS_FALLBACK;
}

/**
 * Credits the starting coins if this wallet has never been seen, and returns
 * how many were granted (`0` when nothing was).
 *
 * Synchronous, matching `EconomyService.credit()`. On the main site the server
 * round-trip it triggers is fire-and-forget with its own retry/backoff
 * (Module 23), so nothing here waits on the network before the first frame.
 */
export function grantStartingCoinsIfNew({
  economy,
  remoteConfig,
  hasExistingWallet,
}: StartingGrantOptions): number {
  if (hasExistingWallet) return 0;
  const amount = configuredStartingCoins(remoteConfig);
  economy.credit('coins', amount, STARTING_GRANT_SOURCE_ID);
  return amount;
}
